// Utility functions for handling analytics settings

/**
 * Key used to store the analytics preference in localStorage
 */
const SKIP_ANALYTICS_KEY = 'skipAnalytics';

/**
 * Check if analytics should be skipped
 * @returns boolean indicating if analytics should be skipped
 */
export function shouldSkipAnalytics(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    // First check localStorage
    const skipAnalyticsStorage = localStorage.getItem(SKIP_ANALYTICS_KEY);
    if (skipAnalyticsStorage === 'true') return true;
    
    // Then check cookie (which might be set by middleware)
    const cookies = document.cookie.split('; ');
    const skipAnalyticsCookie = cookies
      .find(row => row.startsWith(`${SKIP_ANALYTICS_KEY}=`))
      ?.split('=')[1];
    
    if (skipAnalyticsCookie === 'true') {
      // Sync the localStorage with the cookie
      localStorage.setItem(SKIP_ANALYTICS_KEY, 'true');
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error reading analytics preference:', error);
    return false;
  }
}

/**
 * Enable or disable analytics
 * @param skip - boolean indicating if analytics should be skipped
 */
export function setSkipAnalytics(skip: boolean): void {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(SKIP_ANALYTICS_KEY, skip.toString());
  } catch (error) {
    console.error('Error setting analytics preference:', error);
  }
}

/**
 * Check URL for skipanalytics parameter and set preference if present
 * @returns boolean indicating if the parameter was found and processed
 */
export function checkAndSetAnalyticsFromURL(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    const params = new URLSearchParams(window.location.search);
    const skipAnalytics = params.get(SKIP_ANALYTICS_KEY);
    
    if (skipAnalytics === 'true') {
      setSkipAnalytics(true);
      return true;
    } else if (skipAnalytics === 'false') {
      setSkipAnalytics(false);
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error processing skipanalytics parameter:', error);
    return false;
  }
} 